import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { NavBar } from '@design-system/NavBar';

interface IngredientDetailSkeletonProps {
  'data-testid'?: string;
}

export const IngredientDetailSkeleton = ({ 'data-testid': testId }: IngredientDetailSkeletonProps = {}) => {
  return (
    <div className="w-full h-full" data-testid={testId}>
      <section 
        data-testid="ingredientDetailSkeleton-mainSection"
        className="grid mx-auto container p-4 md:px-8 lg:px-24 lg:py-6 max-w-[960px]"
      >
        <NavBar data-testid="ingredientDetailSkeleton-navbar" className='md:-ml-5' />

        <Skeleton 
          data-testid="ingredientDetailSkeleton-title" 
          className="h-12 w-3/4 mt-20 mb-4" 
        /> 
        <Skeleton 
          data-testid="ingredientDetailSkeleton-groupInfo" 
          className="h-6 w-1/2 mb-2" 
        /> 
        <Skeleton 
          data-testid="ingredientDetailSkeleton-productInfo" 
          className="h-6 w-1/3 mb-2" 
        /> 

        <div 
          data-testid="ingredientDetailSkeleton-badgesContainer"
          className="flex gap-4 overflow-x-auto py-4"
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <div 
              key={index} 
              data-testid={`ingredientDetailSkeleton-badge-${index}`}
              className="flex flex-col items-center gap-2"
            >
              <Skeleton className="h-24 w-24 rounded-lg" />
            </div>
          ))}
        </div>

        <div 
          data-testid="ingredientDetailSkeleton-descriptionContainer"
          className="mt-8 mb-6 space-y-2"
        >
          <Skeleton 
            data-testid="ingredientDetailSkeleton-descriptionLine1" 
            className="h-4 w-full" 
          />
          <Skeleton 
            data-testid="ingredientDetailSkeleton-descriptionLine2" 
            className="h-4 w-full" 
          />
          <Skeleton 
            data-testid="ingredientDetailSkeleton-descriptionLine3" 
            className="h-4 w-4/6" 
          />
        </div>

        <div data-testid="ingredientDetailSkeleton-healthImprovementSection">
          <Skeleton 
            data-testid="ingredientDetailSkeleton-healthImprovementTitle" 
            className="h-8 w-48 mb-4 mt-8" 
          /> 
          <div 
            data-testid="ingredientDetailSkeleton-scoreCardsGrid"
            className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1"
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton 
                key={index} 
                data-testid={`ingredientDetailSkeleton-scoreCard-${index}`}
                className="h-24 w-full rounded-lg"
              />
            ))}
          </div>
        </div>
      </section>

      <div 
        data-testid="ingredientDetailSkeleton-supportingResearchSection"
        className="bg-blue-950 p-8 mt-8"
      >
        <Skeleton 
          data-testid="ingredientDetailSkeleton-supportingResearchTitle" 
          className="h-8 w-48 mb-8 bg-gray-400" 
        />
        <div 
          data-testid="ingredientDetailSkeleton-researchLinksContainer"
          className="space-y-8"
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <div 
              key={index} 
              data-testid={`ingredientDetailSkeleton-researchLink-${index}`}
              className="space-y-4"
            >
              <Skeleton className="h-4 w-1/2 bg-gray-400" />
              <Skeleton className="h-px w-full bg-gray-400/30" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};